
////////////////////////////
// FORMATTER UTILITIES

import { colors, size } from '@styles/theme'
import styled from '@emotion/styled/macro'
import capitalize from './capitalize'

const StatTag = styled.span`
  display; flex;
  align-items: center;
  background-color: rgba(100,100,100,0.25);
  border-radius: 100px;
  padding: ${size(0.625)} ${size(1.25)} ${size(0.625)} ${size(1.25)};
  flex-shrink: 0;
`

////////////////////////////
const nearbyProfileStatKeyFormat = {
  hostLocation: 'Availability',
  sexuallyInto: 'Into',
  lookingFor: 'Looking For',
  rolesSituations: 'Roles &\nSituations',
  hivStatus: 'HIV Status',
}

const nearbyProfileStatKeyOrder = [
  'hostLocation',
  'sexuallyInto',
  'lookingFor',
  'kinks',
  'rolesSituations',
  'interactions',
  'fetishes',
  'practices',
  'hivStatus',
]

const atAGlanceKeys = [
  'gender',
  'age',
  'position',
  'height',
  'weight',
  'endowmentSize',
  'endowmentCut',
  'bodyType',
  'sexuality',
  'expression',
]

const keyExists = (extendedInfos, key) => {
  const isString = typeof extendedInfos[key] === 'string'
  const isArray = Array.isArray(extendedInfos[key])
  if (!isString && !isArray && !!extendedInfos[key]) {
    return true
  } else if (isString && !extendedInfos[key].includes('unspecified')) {
    return true
  } else if (isArray && extendedInfos[key].length > 0) {
    return true
  }
}

const hasStats = (extendedInfos) => {
  if (!extendedInfos) return false;
  for (let i = 0; i < nearbyProfileStatKeyOrder.length; i++) {
    if (keyExists(extendedInfos, nearbyProfileStatKeyOrder[i])) {
      return true
    }
  }
  return false
}

const hasAtAGlance = (extendedInfos) => {
  if (!extendedInfos) return false;
  for (let i = 0; i < atAGlanceKeys.length; i++) {
    if (keyExists(extendedInfos, atAGlanceKeys[i])) {
      return true
    }
  }
  return false
}

const valueMap = {
  // remove unspecified
  'endowment cut unspecified': '',
  'body type unspecified': '',
  'gender unspecified': '',
  'sexuality unspecified': '',
  'position unspecified': '',
  'practices unspecified': '',
  'hiv status unspecified': '',
  // sexuality
  'sexuality other': 'Other',
  'bi curious': 'Bicurious',
  // position
  'position other': 'Other',
  // body type
  'body type average': 'Average',
  // expression
  'male to female': 'MTF',
  'female to male': 'FTM',
  // into
  'oral receive only': 'Oral (Receive Only)',
  'oral give only': 'Oral (Give Only)',
  // looking_for
  'u plus': 'U+',
  // interactions
  'flipping swapping': 'Flipping/Swapping',
  // HIV Status
  'neg on prep': 'Negative (On Prep)',
  // kinks
  'gags and tape': 'Gags/Tape',
  'photos videos': 'Photos/Vidoes',
  // roles and situations
  'doctor patient': 'Doctor/Patient',
  'slave master': 'Slave/Master',
  // interactions
  'flipping swapping': 'Flipping/Swapping',
  'rough aggressive': 'Rough/Aggressive',
  // fetishes
  'spandex lycra': 'Spandex/Lycra',
  'mormon undergarments': 'Mormon Garments',
}

const mapValue = (value) => valueMap[value] ?? (value || '');

const mapMatchType = {
  lookingFor: 'expression',
  expression: 'lookingFor',
  sexuallyInto: 'sexuallyInto',
  kinks: 'kinks',
  rolesSituations: 'rolesSituations',
  interactions: 'interactions',
  fetishes: 'fetishes',
}

const mapMatchValues = {
  'oral_receive_only': 'oral_give_Only',
  'oral_give_only': 'oral_receive_only',
}

const isMatch = (statType, value, currentUser) => {
  const matchType = mapMatchType[statType]
  if (!matchType) return false
  const matchValue = mapMatchValues[value] || value
  // This is too inefficient. We should turn the user's extended info into a set higher up the call stack and reference it here.
  return currentUser?.extendedInfos?.[matchType]?.find?.(val => val === matchValue)
}

// format utility functions
const singleEnumFormatter = (enumValue) => {
  if (typeof enumValue === 'string') {
    return enumValue?.replaceAll?.('_', ' ')
  } else {
    return ''
  }
}

const multiEnumFormatter = (enumValue, { slashSeparator = false, currentUser = {}, statType = '' } = {}) => {
  if (!enumValue || !Array.isArray(enumValue)) return ''

  const values = enumValue
    .map(val => ({ value: (typeof val === 'string' ? val.replaceAll('_', ' ') : val), isMatch: isMatch(statType, val, currentUser) }))
    .map(valObj => ({ ...valObj, value: mapValue(valObj.value) }))
    .map(valObj => ({ ...valObj, value: capitalize(valObj.value) }))
  
  if (slashSeparator) {
    return values.reduce((acc, { value }, index) => acc + (enumValue.length - 1 !== index ? `${value}/` : value), '')
  } else {
    return values.map(({ value, isMatch }) => (
      <StatTag className='stat-tag' style={isMatch ? { backgroundColor: colors.statusColors['right_now']('primary'), color: 'white' } : {}} >
        {value}
      </StatTag>
    ))
  }
}


////////////////////////////
// FORMATTER FUNCTIONS
////////////////////////////
const formatAge = (stat) => {
  if (stat && stat !== 0) {
    return stat
  } else {
    return ''
  }
}

const formatHeight = (cm) => {
  if (cm && cm !== 0) {
    const inches = cm/2.54
    const feet = Math.floor(inches/12)
    const remainder = Math.floor(inches%12)
    let result = (remainder > 0) ? `${feet}' ${remainder}"` : `${feet}'`
    return result
  } else {
    return ''
  }
}

const formatWeight = (kg) => {
  if (kg && kg !== 0) {
    return `${Math.round(kg*2.2)}lbs${kg >= 159 ? '+' : ''}`
  } else {
    return ''
  }
}

// TODO: This will have to be redone to accept cm values from the backend
const formatEndowmentSize = (cm) => {
  if (cm && cm !== 0) {
    const inches = (cm/2.54).toFixed(1)
    return `${inches}"`
  } else {
    return ''
  }
}

const formatEndowmentCut = (stat, options) => {
  return mapValue(singleEnumFormatter(stat, options))
};

const formatBodyType = (stat, options) => {
  return mapValue(singleEnumFormatter(stat, options))
}

const formatGender = (stat, options) => {
  return mapValue(singleEnumFormatter(stat, options))
}

const formatGenderAndAge = (stat=[]) => {
  const [gender, age] = stat
  const enumVal = singleEnumFormatter(gender)
  let formattedGender = enumVal?.slice?.(0, 1)?.toUpperCase?.() || ''
  if (formattedGender === 'G') formattedGender = ''
  return `${formattedGender}${age || ''}`
}

const formatSexuality = (stat, options) => {
  return mapValue(singleEnumFormatter(stat, options))
}

const formatPosition = (stat, options) => {
  return mapValue(singleEnumFormatter(stat, options))
}

const formatExpression = multiEnumFormatter

const formatExpressionAtAGlance = (stat) => (
  multiEnumFormatter(stat, { slashSeparator: true })
)

const formatHostLocation = multiEnumFormatter

const formatSexuallyInto = multiEnumFormatter

const formatLookingFor = multiEnumFormatter

const formatKinks = multiEnumFormatter

const formatRolesSituations = multiEnumFormatter

const formatInteractions = multiEnumFormatter

const formatFetishes = multiEnumFormatter

const formatPractices = (stat, options) => {
  return mapValue(singleEnumFormatter(stat, options))
}

const formatHivStatus = (stat, options) => {
  return mapValue(singleEnumFormatter(stat, options))
}

const formatOptions = {
  age: formatAge,
  height: formatHeight,
  weight: formatWeight,
  endowmentSize: formatEndowmentSize,
  endowmentCut: formatEndowmentCut,
  bodyType: formatBodyType,

  gender: formatGender,
  genderAndAge: formatGenderAndAge,
  expression: formatExpression,
  expressionAtAGlance: formatExpressionAtAGlance,
  sexuality: formatSexuality,
  position: formatPosition,

  hostLocation: formatHostLocation,
  sexuallyInto: formatSexuallyInto,
  lookingFor: formatLookingFor,
  interactions: formatInteractions,
  kinks: formatKinks,
  rolesSituations: formatRolesSituations,
  fetishes: formatFetishes,

  practices: formatPractices,
  hivStatus: formatHivStatus,
}

const formatStat = (statType, stat, options={ currentUser: {} }) => {
  if (formatOptions[statType]) return formatOptions[statType](stat, { ...options, statType })
}

const renderStat = (stat, reference) => {
  if (!stat || !reference) return
  
  // Turn set values into arrays
  let value = reference[stat]
  if (value instanceof Set) value = [...value]

  // format the stat
  let formattedStat = formatStat(stat, value)
  if (typeof formattedStat === 'string') {
    formattedStat = capitalize(formattedStat)
  } else if (stat instanceof Array) {
    stat.map(val => typeof val === 'string' ? capitalize(val) : val)
  }

  return formattedStat
}

export default formatStat

export { nearbyProfileStatKeyFormat, nearbyProfileStatKeyOrder, hasStats, hasAtAGlance, StatTag, renderStat, mapValue }
